import React, { useState, useEffect, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { unityBridge } from '../unity-bridge';
import { googleAuthService } from '../google-auth-service';
import { googleSheetsService } from '../google-sheets-service';
import { downloadFile, generateCSV, extractUniqueTableKeys, mergeUnityWithSheetValues } from '../utils';
import { convertQuillToTMP } from '../tmp-quill-converter';
import { tableSyncManager } from '../services/table-sync-manager';
import { dynamicSheetManager } from '../services/dynamic-sheet-manager';
import { GOOGLE_CONFIG } from '../app-config';
import NotificationToast from './modal_components/NotificationToast';
import type {
  ModalMode,
  ModalState,
  TabId,
  CSVData,
  QuillInstances,
  UnityData,
  UpdatedEntry,
  SortOrder
} from '../types';

import { EditTab } from './modal_components/EditTab';
import { ReferencesTab } from './modal_components/ReferencesTab';
import { DebugTab } from './modal_components/DebugTab';
import { SaveProgressModal, SaveStep } from './modal_components/SaveProgressModal';
import EditableGoogleSheetsTab from './modal_components/EditableGoogleSheetsTab';

type AuthPhase = 'initializing' | 'ready' | 'authorizing' | 'authorized' | 'error';


const sanitizeLanguageList = (languages: readonly string[]): string[] => {
  const trimmed = languages
    .map(language => (typeof language === 'string' ? language.trim() : ''))
    .filter((language): language is string => language.length > 0);
  return Array.from(new Set(trimmed));
};

const computeDefaultMode = (_languages: readonly string[]): ModalMode =>
  'standard'; // Always default to standard mode regardless of language count

const pickSecondaryLanguage = (languages: readonly string[], primary: string): string => {
  const secondary = languages.find(language => language && language !== primary);
  return secondary ?? '';
};

const EditModeApp: React.FC = () => {
  const [authState, setAuthState] = useState<AuthPhase>('initializing');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFirstOpen, setIsFirstOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSteps, setSaveSteps] = useState<SaveStep[]>([]);
  const [showSaveProgress, setShowSaveProgress] = useState(false);
  const localBackupEnabled = !!(GOOGLE_CONFIG.features?.enableLocalBackupDownload ?? true);
  const configuredLanguages = React.useMemo(() => {
    const mapping = GOOGLE_CONFIG.languages?.columnMapping ?? {};
    const languagesFromMapping = sanitizeLanguageList(Object.keys(mapping));
    if (languagesFromMapping.length > 0) {
      return languagesFromMapping;
    }

    const defaults = GOOGLE_CONFIG.languages?.defaultLanguages ?? [];
    return sanitizeLanguageList(defaults);
  }, []);

  const fallbackPrimaryLanguage = configuredLanguages[0] ?? '';
  const fallbackSecondaryLanguage = pickSecondaryLanguage(configuredLanguages, fallbackPrimaryLanguage);
  const initialAvailableLanguages = configuredLanguages.length > 0
    ? [...configuredLanguages]
    : [];

  const [currentLanguage, setCurrentLanguage] = useState<string>(fallbackPrimaryLanguage);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>(initialAvailableLanguages);
  const modalLanguageOptions = React.useMemo(() => {
    const sanitizedAvailable = sanitizeLanguageList(availableLanguages);
    return sanitizedAvailable.length > 0 ? sanitizedAvailable : configuredLanguages;
  }, [availableLanguages, configuredLanguages]);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isUnityReady, setIsUnityReady] = useState(false);
  const [isSceneChanging, setIsSceneChanging] = useState(false);
  const [sceneChangeMessage, setSceneChangeMessage] = useState('');
  const [unityLanguage, setUnityLanguage] = useState<string>('Default Language');

  // Load saved mode from localStorage, fallback to computed default
  const loadSavedMode = (): ModalMode => {
    try {
      const saved = localStorage.getItem('editMode_preferredMode');
      if (saved === 'standard' || saved === 'translate') {
        return saved;
      }
    } catch (error) {
      console.warn('[EDIT MODE] Failed to load saved mode from localStorage:', error);
    }
    return computeDefaultMode(initialAvailableLanguages);
  };

  // Load saved sort order from localStorage, fallback to 'hierarchy'
  const loadSavedSortOrder = (): SortOrder => {
    try {
      const saved = localStorage.getItem('editMode_preferredSortOrder');
      if (saved === 'size-desc' || saved === 'size-asc' || saved === 'alpha' || saved === 'hierarchy' || saved === 'hierarchy-reversed') {
        return saved as SortOrder;
      }
    } catch (error) {
      console.warn('[EDIT MODE] Failed to load saved sort order from localStorage:', error);
    }
    return 'hierarchy'; // Default to Unity Hierarchy
  };

  const [modalState, setModalState] = useState<ModalState>({
    currentMode: loadSavedMode(),
    standardLanguage: fallbackPrimaryLanguage,
    languageA: fallbackPrimaryLanguage,
    languageB: fallbackSecondaryLanguage,
    numFields: 50,
    sortOrder: loadSavedSortOrder(),
    searchTerm: '',
    activeTab: 'edit-text',
    selectedTables: new Set(),
    showAllTables: true
  });

  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [lastReceivedJson, setLastReceivedJson] = useState('');
  const [enrichedUnityData, setEnrichedUnityData] = useState<string>('');
  const [isLoadingTemplateValues, setIsLoadingTemplateValues] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const quillInstancesRef = useRef<QuillInstances>({});

  // Helper function to wait for auth to complete
  const waitForAuth = (): Promise<void> => {
    return new Promise((resolve) => {
      // Check auth state via googleAuthService directly
      const checkIfAuthorized = () => {
        return googleAuthService.getAccessToken() !== null;
      };

      // If already authorized, resolve immediately
      if (checkIfAuthorized()) {
        resolve();
        return;
      }

      console.log('[EDIT MODE] Waiting for user to complete Google sign-in...');

      // Otherwise, poll for auth completion
      const checkAuth = setInterval(() => {
        if (checkIfAuthorized()) {
          clearInterval(checkAuth);
          console.log('[EDIT MODE] Auth detected, proceeding...');
          resolve();
        }
      }, 100); // Check every 100ms

      // Optional: Add timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkAuth);
        console.warn('[EDIT MODE] Auth wait timeout after 2 minutes, proceeding anyway');
        // Resolve anyway to avoid infinite wait (will fail later with better error)
        resolve();
      }, 120000); // 2 minute timeout
    });
  };

  // Initialize Google Auth service
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    googleAuthService
      .initialize()
      .then(() => {
        unsubscribe = googleAuthService.onAuthStateChange(({ authorized, error, token }) => {
          setAuthError(error);
          if (authorized && token) {
            googleSheetsService.setAccessToken(token);
            setAuthState('authorized');
            console.log('[EDIT MODE] Google Auth ready with token, ready to use edit mode');
          } else {
            googleSheetsService.setAccessToken(null);
            setAuthState('ready');
            if (error) {
              console.error('[EDIT MODE] Google Auth error:', error);
            }
          }
        });
      })
      .catch((error) => {
        setAuthState('error');
        setAuthError('Failed to initialize Google authentication. Please refresh the page and try again.');
        console.error('[EDIT MODE] Failed to initialize Google Auth:', error);
      });

    return () => {
      unsubscribe?.();
    };
  }, []);

  // Set up global handler for language change notifications from Unity
  useEffect(() => {
    // Register global function for Unity to call when language changes
    window.notifyLanguageChanged = (localeName: string) => {
      console.log('[EDIT MODE] Received language change notification from Unity:', localeName);
      setUnityLanguage(localeName);
    };

    return () => {
      delete window.notifyLanguageChanged;
    };
  }, []);

  // Initialize Unity bridge when Unity is ready
  useEffect(() => {
    const checkUnityReady = () => {
      if (window.unityInstance) {
        console.log('[EDIT MODE] Unity instance detected, initializing bridge...');
        unityBridge.initialize(window.unityInstance);

        // Register data received callback
        unityBridge.onDataReceived(async (data: UnityData) => {
          console.log('[EDIT MODE] Data received from Unity:', data);

          // Start loading
          setIsLoadingTemplateValues(true);
          setLoadingStatus('Receiving data from Unity...');

          try {
            // Store raw Unity data for debugging
            setLastReceivedJson(JSON.stringify(data));
            window.lastReceivedJson = JSON.stringify(data);

            // Extract available languages from Unity data if available
            if (data.availableLanguages && data.availableLanguages.length > 0) {
              const sanitizedLanguages = sanitizeLanguageList(data.availableLanguages);
              if (sanitizedLanguages.length > 0) {
                console.log('[EDIT MODE] Languages received from Unity:', sanitizedLanguages);
                setAvailableLanguages(sanitizedLanguages);
                setCurrentLanguage(sanitizedLanguages[0]);
              }
            }

            // Extract current language from Unity if available
            if (data.currentLanguage) {
              setUnityLanguage(data.currentLanguage);
              console.log('[EDIT MODE] Unity is displaying language:', data.currentLanguage);
            }

            // Update status: extracting keys
            setLoadingStatus('Extracting unique keys...');

            // ⚠️ AUTH GUARD: Wait for auth if not ready
            if (!googleAuthService.getAccessToken()) {
              console.log('[EDIT MODE] Auth not ready, waiting for sign-in completion...');
              setLoadingStatus('Waiting for Google sign-in to complete...');
              await waitForAuth();
              console.log('[EDIT MODE] Auth completed, proceeding with fetch');
            }

            // Determine which languages to fetch (use available languages from Unity/config)
            const sanitizedLanguages = sanitizeLanguageList(data.availableLanguages || configuredLanguages);
            const languagesToFetch = sanitizedLanguages.length > 0 ? sanitizedLanguages : ['Default Language'];

            console.log(`[EDIT MODE] Will fetch template values for ${languagesToFetch.length} languages:`, languagesToFetch);

            // Fetch template values for ALL languages
            const allTemplateValuesByLanguage = new Map<string, Map<string, string>>();

            for (const language of languagesToFetch) {
              setLoadingStatus(`Fetching ${language} from Google Sheets...`);

              // Extract unique keys for this language
              const keysToFetch = extractUniqueTableKeys(data.textEntries, language);
              console.log(`[EDIT MODE] Fetching ${keysToFetch.length} keys for language "${language}"`);

              // Fetch template values for this language
              const templateValues = await dynamicSheetManager.fetchTemplateValuesByKeys(keysToFetch);
              console.log(`[EDIT MODE] Received ${templateValues.size} template values for "${language}"`);

              // Store the template values for this language
              allTemplateValuesByLanguage.set(language, templateValues);
            }

            // Update status: merging data
            setLoadingStatus('Merging Unity data with Google Sheets...');

            // Merge Unity metadata with ALL language template values
            const enrichedEntries = data.textEntries.map(entry => {
              const enrichedEntry = { ...entry };

              // Store the original Unity text as runtimeValue
              enrichedEntry.runtimeValue = entry.text || '';

              // Initialize templateValuesByLanguage object
              enrichedEntry.templateValuesByLanguage = {};

              // For each language, try to find the template value
              let foundAnyTemplate = false;
              for (const language of languagesToFetch) {
                const templateValuesForLang = allTemplateValuesByLanguage.get(language);
                if (templateValuesForLang && entry.tableName && entry.key) {
                  const compositeKey = `${entry.tableName}|${entry.key}|${language}`;
                  const templateValue = templateValuesForLang.get(compositeKey);

                  if (templateValue !== undefined) {
                    enrichedEntry.templateValuesByLanguage[language] = templateValue;
                    foundAnyTemplate = true;
                  }
                }
              }

              // Set primary display values for backward compatibility (use first language)
              const primaryLanguage = languagesToFetch[0];
              if (
                Object.prototype.hasOwnProperty.call(
                  enrichedEntry.templateValuesByLanguage,
                  primaryLanguage
                )
              ) {
                const templateValue = enrichedEntry.templateValuesByLanguage[primaryLanguage] ?? '';
                enrichedEntry.text = templateValue;
                enrichedEntry.templateValue = templateValue;
                enrichedEntry.missingFromSheets = false;
              } else {
                // Not found in any language - mark as missing
                enrichedEntry.missingFromSheets = true;
                enrichedEntry.text = enrichedEntry.runtimeValue;
              }

              return enrichedEntry;
            });

            // Create enriched data object
            const enrichedData = {
              ...data,
              textEntries: enrichedEntries
            };

            setEnrichedUnityData(JSON.stringify(enrichedData));
            console.log('[EDIT MODE] Data enriched with Google Sheets template values');

            // Update status: ready
            setLoadingStatus('Ready!');

            // Brief delay to show "Ready!" status
            setTimeout(() => {
              setIsLoadingTemplateValues(false);
              setLoadingStatus('');
            }, 500);
          } catch (error) {
            console.error('[EDIT MODE] Failed to fetch template values from sheets:', error);
            // Fallback: use Unity data as-is
            setEnrichedUnityData(JSON.stringify(data));
            setLoadingStatus('Error loading data - using Unity data only');

            // Clear loading state after error
            setTimeout(() => {
              setIsLoadingTemplateValues(false);
              setLoadingStatus('');
            }, 2000);
          }
        });

        // Register scene change callback
        unityBridge.onSceneChanged(() => {
          console.log('[EDIT MODE] Scene change detected');
          setIsSceneChanging(true);
          setSceneChangeMessage('Loading data...');

          // Scene change handling
          setTimeout(() => {
            setIsSceneChanging(false);
            setSceneChangeMessage('');
          }, 1500);
        });

        setIsUnityReady(true);

        // Request Unity to send its current status and available languages
        setTimeout(() => {
          unityBridge.requestUnityStatus();
        }, 500);

        return true;
      }
      return false;
    };

    // Try immediately
    if (!checkUnityReady()) {
      // Poll for Unity readiness
      const pollInterval = setInterval(() => {
        if (checkUnityReady()) {
          clearInterval(pollInterval);
        }
      }, 500);

      // Clean up after timeout
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
        console.warn('[EDIT MODE] Unity instance not found after timeout');
      }, 30000);

      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }
  }, []);

  // Load CSV data from Google Sheets
  const loadCSV = useCallback(async () => {
    // CSV loading is no longer needed - we use dynamic sheet manager now
    // Each table has its own spreadsheet configured in app-config.ts
    console.log('[EDIT MODE] CSV loading skipped - using dynamic sheet manager');

    // Just clear the loading state
    setIsLoading(false);
    setLoadingMessage('');

    // Set empty CSV data to avoid issues with legacy code
    setCsvData([]);
    window.csvData = [];
  }, []);


  // Handle save changes
  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    setSaveError(null);

    // Initialize progress steps
    const steps: SaveStep[] = [
      { id: 'collect', label: 'Collecting changes from editors', status: 'pending' },
      { id: 'unity', label: 'Sending updates to Unity WebGL', status: 'pending' },
      { id: 'sheets', label: 'Updating Google Sheets', status: 'pending' }
    ];

    if (localBackupEnabled) {
      steps.push({ id: 'download', label: 'Creating local backup', status: 'pending' });
    }

    setSaveSteps(steps);
    setShowSaveProgress(true);

    const updateStep = (id: string, updates: Partial<SaveStep>) => {
      setSaveSteps(prev => prev.map(step =>
        step.id === id ? { ...step, ...updates } : step
      ));
    };

    try {
      // Step 1: Collect changes
      updateStep('collect', { status: 'in-progress' });

      const updatedEntries: UpdatedEntry[] = [];

      // Collect changes from quill instances
      Object.keys(quillInstancesRef.current).forEach(id => {
        const instance = quillInstancesRef.current[id];
        if (!instance || !instance.quill) return;

        const currentHTML = instance.quill.root.innerHTML;
        if (currentHTML !== instance.originalText) {
          const delta = instance.quill.getContents();
          const tmpText = convertQuillToTMP({ ops: delta.ops });

          // Check if this Quill instance represents a deduplicated entry
          const deduplicatedEntry = (instance as any).deduplicatedEntry;

          if (deduplicatedEntry) {
            // This is a deduplicated entry - expand to all original entries
            console.log(`[EDIT MODE] Expanding deduplicated entry "${deduplicatedEntry.key}" to ${deduplicatedEntry.instanceCount} GameObjects`);

            deduplicatedEntry.originalEntries.forEach((originalEntry: any) => {
              const entryWithTable = {
                id: originalEntry.id,
                path: originalEntry.path,
                key: originalEntry.key || deduplicatedEntry.key,
                text: tmpText,
                language: instance.language || currentLanguage,
                tableName: originalEntry.tableReference?.tableName || originalEntry.tableName || deduplicatedEntry.tableName
              } as UpdatedEntry;

              updatedEntries.push(entryWithTable);
            });
          } else {
            // Regular entry - single update
            const entryWithTable = {
              id: instance.entry.id,
              path: instance.entry.path,
              key: instance.entry.key || '',
              text: tmpText,
              language: instance.language || currentLanguage,
              tableName: instance.entry.tableReference?.tableName || instance.entry.tableName
            } as UpdatedEntry;

            updatedEntries.push(entryWithTable);
          }
        }
      });

      await new Promise(resolve => setTimeout(resolve, 500));
      updateStep('collect', { status: 'success' });

      if (updatedEntries.length === 0) {
        console.log('No changes to save');
        setShowSaveProgress(false);
        setIsSaving(false);
        return;
      }

      // Step 2: Send to Unity
      updateStep('unity', { status: 'in-progress' });
      try {
        // Send ALL updates to Unity (no language filtering)
        // Unity's PersistLocalizationEntry will handle persisting to the in-memory localization table
        // Note: Each language has its own table in Unity's localization system
        console.log(`[EDIT MODE] Sending ${updatedEntries.length} updates to Unity (all languages)`);

        // Send all updates to Unity
        unityBridge.sendTextUpdates(updatedEntries);
        await new Promise(resolve => setTimeout(resolve, 300));
        updateStep('unity', { status: 'success' });
      } catch (error) {
        updateStep('unity', {
          status: 'error',
          error: 'Failed to sync with Unity. Please try again.'
        });
      }

      // Step 3: Update Google Sheets using TableSyncManager
      updateStep('sheets', { status: 'in-progress' });
      try {
        console.log(`[EDIT MODE] Using TableSyncManager to sync ${updatedEntries.length} entries across multiple tables`);

        // Use TableSyncManager for multi-table sync
        const syncResult = await tableSyncManager.syncEditsByTable(
          updatedEntries,
          currentLanguage
        );

        // Update progress based on sync results
        if (syncResult.overallStatus === 'success') {
          updateStep('sheets', {
            status: 'success',
            details: syncResult.summary
          });
          console.log('[EDIT MODE] Multi-table sync successful:', syncResult.summary);
        } else if (syncResult.overallStatus === 'partial') {
          updateStep('sheets', {
            status: 'warning', // This might need to be added to SaveStep types
            details: syncResult.summary
          });
          console.warn('[EDIT MODE] Partial sync:', syncResult.summary);
        } else {
          updateStep('sheets', {
            status: 'error',
            error: syncResult.summary
          });
          console.error('[EDIT MODE] Multi-table sync failed:', syncResult.summary);
        }

        // Log detailed results for each table
        syncResult.tableResults.forEach(result => {
          console.log(`[EDIT MODE] Table "${result.tableName}": ${result.status}, ${result.updatedCount}/${result.totalCount} entries, ${result.details || result.error || 'no details'}`);
        });

      } catch (error) {
        console.error('[EDIT MODE] Failed to update Google Sheets:', error);

        // Extract specific permission errors from sync results if available
        let errorMessage = 'Failed to update Google Sheets.';
        if (error && typeof error === 'object' && 'tableResults' in error) {
          const failedTables = (error as any).tableResults
            ?.filter((r: any) => r.status === 'error' && r.error)
            ?.map((r: any) => `${r.tableName}: ${r.error}`)
            ?.filter((msg: string) => msg.includes('permission') || msg.includes('access'));

          if (failedTables?.length > 0) {
            errorMessage = `Permission errors:\n${failedTables.join('\n')}`;
          }
        } else if (error instanceof Error && error.message) {
          errorMessage = error.message;
        }

        updateStep('sheets', {
          status: 'error',
          error: errorMessage
        });
      }

      // Step 4: Download locally (optional)
      if (localBackupEnabled) {
        updateStep('download', { status: 'in-progress' });
        try {
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const filename = `localization_backup_${timestamp}.csv`;

          if (csvData) {
            const csvContent = generateCSV(csvData, updatedEntries);
            downloadFile(filename, csvContent);
          }

          await new Promise(resolve => setTimeout(resolve, 200));
          updateStep('download', { status: 'success' });
        } catch (error) {
          updateStep('download', {
            status: 'error',
            error: 'Failed to create backup file.'
          });
        }
      }

      // Mark as saved
      Object.keys(quillInstancesRef.current).forEach(id => {
        const instance = quillInstancesRef.current[id];
        if (instance && instance.quill) {
          instance.originalText = instance.quill.root.innerHTML;
        }
      });

      // Wait to show completion
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Close modal on successful save
      setShowSaveProgress(false);
      setIsModalOpen(false);
      unityBridge.notifyModalClosed();
    } catch (error) {
      console.error('Save failed:', error);
      setSaveError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [csvData, currentLanguage, localBackupEnabled, unityLanguage]);

  // Clear Quill instances
  const clearQuillInstances = useCallback(() => {
    Object.keys(quillInstancesRef.current).forEach(id => {
      const instance = quillInstancesRef.current[id];
      if (instance?.quill) {
        instance.quill.off('text-change');
        instance.quill.off('selection-change');
        instance.quill.off('editor-change');
        const container = instance.quill.root.parentElement;
        if (container && container.parentElement) {
          const toolbar = container.parentElement.querySelector('.quill-toolbar');
          if (toolbar) toolbar.remove();
          container.innerHTML = '';
        }
      }
      delete quillInstancesRef.current[id];
    });
    setHasChanges(false);
    setSaveError(null);
  }, []);


  // Handle modal open
  const handleOpenModal = useCallback(async () => {
    if (!isUnityReady) {
      console.warn('[EDIT MODE] Unity not ready, cannot open modal');
      return;
    }

    clearQuillInstances();

    const languagesForModal = modalLanguageOptions;

    let resolvedPrimaryLanguage = currentLanguage;
    if (!resolvedPrimaryLanguage && languagesForModal.length > 0) {
      resolvedPrimaryLanguage = languagesForModal[0];
      setCurrentLanguage(resolvedPrimaryLanguage);
    }

    const resolvedSecondaryLanguage = pickSecondaryLanguage(languagesForModal, resolvedPrimaryLanguage);

    setModalState(prev => ({
      ...prev,
      // Preserve the saved mode instead of resetting to default
      standardLanguage: resolvedPrimaryLanguage,
      languageA: resolvedPrimaryLanguage,
      languageB: resolvedSecondaryLanguage
    }));

    // Load fresh data
    await loadCSV();

    if (isFirstOpen) {
      setIsFirstOpen(false);
    }

    setIsModalOpen(true);
    unityBridge.notifyModalOpened();

    if (!lastReceivedJson) {
      unityBridge.requestSceneText();
    }
  }, [loadCSV, lastReceivedJson, clearQuillInstances, isFirstOpen, currentLanguage, modalLanguageOptions, isUnityReady]);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    unityBridge.notifyModalClosed();
  }, []);

  // Handle state changes
  const handleStateChange = useCallback((updates: Partial<ModalState>) => {
    setModalState(prev => ({ ...prev, ...updates }));
  }, []);

  // Check for unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return Object.keys(quillInstancesRef.current).some(id => {
      const instance = quillInstancesRef.current[id];
      return instance && instance.quill &&
             instance.quill.root.innerHTML !== instance.originalText;
    });
  }, []);

  // Set up periodic check for changes
  useEffect(() => {
    const checkInterval = setInterval(() => {
      setHasChanges(hasUnsavedChanges());
    }, 500);

    return () => clearInterval(checkInterval);
  }, [hasUnsavedChanges]);

  // Handle language change
  const handleLanguageChange = useCallback((language: string) => {
    console.log(`[EDIT MODE] Changing Unity language to: ${language}`);
    unityBridge.changeLanguage(language);
    setShowLanguageDropdown(false);
  }, []);

  // Cycle to next language
  const handleCycleLanguage = useCallback(() => {
    const currentIndex = availableLanguages.indexOf(unityLanguage);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    const nextLanguage = availableLanguages[nextIndex];
    console.log(`[EDIT MODE] Cycling language from ${unityLanguage} to ${nextLanguage}`);
    handleLanguageChange(nextLanguage);
  }, [availableLanguages, unityLanguage, handleLanguageChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.language-dropdown-container')) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showLanguageDropdown]);

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('editMode_preferredMode', modalState.currentMode);
      console.log(`[EDIT MODE] Saved preferred mode to localStorage: ${modalState.currentMode}`);
    } catch (error) {
      console.warn('[EDIT MODE] Failed to save mode to localStorage:', error);
    }
  }, [modalState.currentMode]);

  // Save sort order to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('editMode_preferredSortOrder', modalState.sortOrder);
      console.log(`[EDIT MODE] Saved preferred sort order to localStorage: ${modalState.sortOrder}`);
    } catch (error) {
      console.warn('[EDIT MODE] Failed to save sort order to localStorage:', error);
    }
  }, [modalState.sortOrder]);


  // Handle sign in
  const handleSignIn = useCallback(async () => {
    setAuthState('authorizing');
    try {
      await googleAuthService.signIn();
    } catch (error) {
      setAuthError('Google sign-in failed. Please try again.');
      setAuthState('ready');
    }
  }, []);

  // Handle sign out
  const handleSignOut = useCallback(async () => {
    await googleAuthService.signOut();
    setAuthState('ready');
  }, []);

  // Show loading screen while initializing
  if (authState === 'initializing') {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-xl text-center">
          <h1 className="text-2xl font-semibold mb-4">Localization Demo Edit Mode</h1>
          <div className="w-12 h-12 border-4 border-gray-200 border-t-unity-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Google services...</p>
        </div>
      </div>
    );
  }

  // Show sign-in screen if not authorized
  if (authState === 'ready' || authState === 'authorizing' || authState === 'error') {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-xl text-center max-w-md">
          <h1 className="text-2xl font-semibold mb-4">Localization Demo Edit Mode</h1>
          <p className="text-gray-600 mb-6">Sign in with a Google account that can edit the demo spreadsheets.</p>
          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {authError}
            </div>
          )}
          <button
            type="button"
            className="px-6 py-3 bg-unity-blue text-white rounded-lg hover:bg-unity-blue-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleSignIn}
            disabled={authState === 'authorizing'}
          >
            {authState === 'authorizing' ? 'Authorizing...' : 'Sign in with Google'}
          </button>
        </div>
      </div>
    );
  }

  // Only show the main edit mode interface when authorized
  if (authState !== 'authorized') {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg shadow-xl text-center">
          <h1 className="text-2xl font-semibold mb-4">Localization Demo Edit Mode</h1>
          <div className="w-12 h-12 border-4 border-gray-200 border-t-unity-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Preparing edit mode...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification Toast */}
      <NotificationToast position="top-right" maxNotifications={3} />

      {/* Save Progress Modal */}
      <SaveProgressModal
        isOpen={showSaveProgress}
        steps={saveSteps}
        onClose={() => setShowSaveProgress(false)}
      />

      {/* Top Control Bar */}
      <div className="fixed top-4 left-4 z-modal-button flex items-center gap-3">
        {/* Edit Mode Button */}
        <button
          onClick={handleOpenModal}
          disabled={!isUnityReady}
          className={clsx(
            "flex items-center gap-2 px-4 py-2.5 rounded-full shadow-md transition-all active:scale-95",
            isUnityReady
              ? "bg-unity-blue hover:bg-unity-blue-dark text-white hover:shadow-lg"
              : "bg-gray-400 text-gray-600 cursor-not-allowed"
          )}
          title={!isUnityReady ? "Waiting for Unity to initialize..." : "Open edit mode"}
          type="button"
        >
          {!isUnityReady ? (
            <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
            </svg>
          )}
          <span>{isUnityReady ? 'Edit Mode' : 'Loading...'}</span>
        </button>

        {/* COMMENTED OUT: Language Dropdown - will be replaced with in-WebGL language UI
            Language switching will be moved to Unity WebGL UI instead of index.html
            This allows better integration with Unity's localization system and avoids
            the complexity of syncing language state between index.html and Unity.
        <div className="language-dropdown-container relative">
          <div
            className={clsx(
              "flex items-center gap-2 px-4 py-2.5 rounded-full shadow-md transition-all",
              isUnityReady && availableLanguages.length > 0
                ? "bg-green-600 text-white"
                : "bg-gray-400 text-gray-600"
            )}
          >
            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M4.545 6.714 4.11 8H3l1.862-5h1.284L8 8H6.833l-.435-1.286H4.545zm1.634-.736L5.5 3.956h-.049l-.679 2.022H6.18z"/>
              <path d="M0 2a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-3H2a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2zm7.138 9.995c.193.301.402.583.63.846-.748.575-1.673 1.001-2.768 1.292.178.217.451.635.555.867 1.125-.359 2.08-.844 2.886-1.494.777.665 1.739 1.165 2.93 1.472.133-.254.414-.673.629-.89-1.125-.253-2.057-.694-2.82-1.284.681-.747 1.222-1.651 1.621-2.757H14V8h-3v1.047h.765c-.318.844-.74 1.546-1.272 2.13a6.066 6.066 0 0 1-.415-.492 1.988 1.988 0 0 1-.94.31z"/>
            </svg>
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              disabled={!isUnityReady || availableLanguages.length === 0}
              className={clsx(
                "flex items-center gap-2 transition-all active:scale-95",
                isUnityReady && availableLanguages.length > 0
                  ? "hover:opacity-90"
                  : "cursor-not-allowed"
              )}
              title={!isUnityReady ? "Waiting for Unity to initialize..." : "Select language"}
              type="button"
            >
              <span>{unityLanguage}</span>
              <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
              </svg>
            </button>
          </div>
          {showLanguageDropdown && availableLanguages.length > 0 && (
            <div
              className="absolute top-full left-0 mt-2 min-w-[220px] bg-white border border-gray-300 rounded-lg shadow-lg z-50 overflow-hidden"
              style={{ maxHeight: '400px', overflowY: 'auto' }}
            >
              <div className="py-1">
                {availableLanguages.map((language) => (
                  <button
                    key={language}
                    onClick={() => handleLanguageChange(language)}
                    className={clsx(
                      "w-full text-left px-4 py-2.5 transition-colors",
                      language === unityLanguage
                        ? "bg-blue-50 text-blue-700 border-l-4 border-blue-500 font-medium"
                        : "text-gray-700 hover:bg-gray-100 border-l-4 border-transparent"
                    )}
                    type="button"
                  >
                    <div className="flex items-center justify-between">
                      <span>{language}</span>
                      {language === unityLanguage && (
                        <svg className="w-4 h-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        END COMMENTED OUT: Language Dropdown */}

        {/* Scene Change Loading Indicator */}
        {isSceneChanging && (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full shadow-sm animate-fade-in">
            <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" />
            <span className="text-sm font-medium text-gray-600">{sceneChangeMessage}</span>
          </div>
        )}
      </div>

      {/* Sign Out Button */}
      <div className="fixed top-4 right-4 z-modal-button">
        <button
          type="button"
          onClick={handleSignOut}
          className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
          title="Sign out of Google"
        >
          Sign Out
        </button>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/70 z-save-progress flex justify-center items-center">
          <div className="bg-white p-8 rounded-lg shadow-xl text-center w-80">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-unity-blue rounded-full animate-spin-slow mx-auto mb-5"></div>
            <h3 className="text-lg font-semibold mb-2">Loading Edit Mode</h3>
            <p className="text-sm text-gray-600">{loadingMessage || 'Connecting to Google Sheets...'}</p>
          </div>
        </div>
      )}

      {/* Modal Overlay */}
      {isModalOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-modal-overlay flex justify-center items-center"
            onClick={(e) => {
              e.stopPropagation();
              if (e.target === e.currentTarget) {
                if (hasChanges) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                    handleCloseModal();
                  }
                } else {
                  handleCloseModal();
                }
              }
            }}
          />

          {/* Edit Modal */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[95%] h-[95%] bg-unity-gray-light rounded-xl shadow-2xl z-modal overflow-hidden font-nunito">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-5 py-2.5 border-b border-unity-gray bg-white rounded-t-xl">
              <div className="text-xl font-semibold text-unity-gray-dark">Edit Mode</div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => {
                    if (hasChanges) {
                      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                        handleCloseModal();
                      }
                    } else {
                      handleCloseModal();
                    }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-unity-gray-light text-gray-700 border border-unity-gray rounded-2xl text-sm font-medium hover:bg-gray-200 transition-all shadow-sm"
                >
                  <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                  Close
                </button>
                <button
                  onClick={handleSaveChanges}
                  disabled={!hasChanges || isSaving}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-sm font-medium transition-all shadow-sm",
                    hasChanges && !isSaving
                      ? "bg-unity-green hover:bg-unity-green-dark text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  )}
                >
                  {isSaving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {saveError && (
              <div className="px-5 py-2 bg-red-50 border-b border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-800">{saveError}</span>
                  </div>
                  <button
                    onClick={() => setSaveError(null)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Modal Body */}
            <div className="flex h-[calc(100%-45px)] w-full overflow-hidden">
              {/* Sidebar */}
              <div className="w-[60px] min-w-[60px] bg-unity-gray-dark flex flex-col py-4">
                {(['edit-text', 'references', 'google-sheets', 'debug-info'] as TabId[]).map((tabId) => (
                  <button
                    key={tabId}
                    onClick={() => handleStateChange({ activeTab: tabId })}
                    className={clsx(
                      "flex justify-center items-center h-[60px] text-gray-400 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer",
                      modalState.activeTab === tabId && "bg-gray-700 text-white"
                    )}
                  >
                    <TabIcon tabId={tabId} />
                  </button>
                ))}
              </div>

              {/* Content Area */}
              <div className="flex-1 flex flex-col overflow-hidden w-[calc(100%-60px)]">
                {modalState.activeTab === 'edit-text' && (
                  <EditTab
                    modalState={modalState}
                    csvData={csvData}
                    lastReceivedJson={enrichedUnityData || lastReceivedJson}
                    quillInstances={quillInstancesRef.current}
                    languageOptions={modalLanguageOptions}
                    onStateChange={handleStateChange}
                    onSaveChanges={handleSaveChanges}
                    onQuillChange={() => setHasChanges(hasUnsavedChanges())}
                    isLoadingTemplateValues={isLoadingTemplateValues}
                    loadingStatus={loadingStatus}
                  />
                )}
                {modalState.activeTab === 'references' && (
                  <ReferencesTab />
                )}
                {modalState.activeTab === 'google-sheets' && (
                  <EditableGoogleSheetsTab />
                )}
                {modalState.activeTab === 'debug-info' && (
                  <DebugTab
                    unityData={lastReceivedJson ? JSON.parse(lastReceivedJson) : null}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const TabIcon: React.FC<{ tabId: TabId }> = ({ tabId }) => {
  const icons = {
    'edit-text': (
      <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
        <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
      </svg>
    ),
    'references': (
      <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
        <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5V2zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1H4z"/>
      </svg>
    ),
    'google-sheets': (
      <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
        <path d="M10 13a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-2zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-2zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1v-2zm10-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V8zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V8zm10-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V3zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3zm-5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V3z"/>
      </svg>
    ),
    'debug-info': (
      <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
        <path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872l-.1-.34zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/>
      </svg>
    )
  };

  return icons[tabId] || null;
};

export default EditModeApp;
